<template>
  <!-- account controls, linked to by the account icon next to logout -->
  <div class="pageContent">
    <div v-if="ready" class="contentView">

      <!-- edit/add employees -->
      <div class="bigSection">
        <addEmployees title="Users" subtitle="Manage your practice" />
      </div>

      <!-- video intro/outros -->
      <div class="bigSection">
        <div class="has-text-left pl-2">
          <p class="title is-3">Video Bookends</p>
          <p class="subtitle is-5">These videos play before and after each playlist you send</p>
        </div>
        <div class="my-4 pt-3 px-4 userRow">
          <div class="columns has-text-left">
            <div class="column">
              <div class="is-flex is-flex-direction-row">
                <p class="title is-4 mb-2 pl-2">Intro</p>
                <b-tooltip type="is-dark" :active="!practice.video_intro" label="Default intros will be used">
                  <b-icon
                    class="ml-1"
                    :icon="practice.video_intro ? 'check-circle' : 'alert-circle'"
                    :type="practice.video_intro ? 'is-success' : 'is-danger'"
                  />
                </b-tooltip>
              </div>
              <fileUpload
                :whiteBG="false"
                uploadType="VIDEO_INTRO"
                labelText="Drag & drop or <span style='text-decoration: underline; cursor: pointer!important;'>browse</span>" 
                fileTypes="video/*"
              />
              <div v-if="practice.video_intro" :key="videoUrlKey">
                <video :src="$root.getURL(practice.video_intro)" controls />
              </div>
            </div>
            <div class="column">
              <div class="is-flex is-flex-direction-row">
                <p class="title is-4 mb-2 pl-2">Outro</p>
                <b-tooltip type="is-dark" :active="!practice.video_outro" label="Default outros will be used">
                  <b-icon
                    class="ml-1"
                    :icon="practice.video_outro ? 'check-circle' : 'alert-circle'"
                    :type="practice.video_outro ? 'is-success' : 'is-danger'"
                  />
                </b-tooltip>
              </div>
              <fileUpload
                :whiteBG="false"
                uploadType="VIDEO_OUTRO"
                labelText="Drag & drop or <span style='text-decoration: underline; cursor: pointer!important;'>browse</span>" 
                fileTypes="video/*"
              />
              <div v-if="practice.video_outro" :key="videoUrlKey">
                <video :src="$root.getURL(practice.video_outro)" controls />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Practice Info: logo, links, name -->
      <div class="bigSection has-text-left">
        <p class="title is-3 pl-2">Practice Info</p>
        <p class="subtitle is-5 pl-2 mb-3">Change what your users see</p>
        <div class="columns">
          <div class="column">
            <div class="userRow py-3 px-4">
              <div class="is-flex is-flex-direction-row">
                <p class="title is-4 pl-2">Logo</p>
                <b-tooltip type="is-dark" :active="!practice.practice_logo" label="You haven't uploaded a practice logo">
                  <b-icon
                    class="ml-1"
                    :icon="practice.practice_logo ? 'check-circle' : 'alert-circle'"
                    :type="practice.practice_logo ? 'is-success' : 'is-danger'"
                  />
                </b-tooltip>
              </div>
              <fileUpload
                :whiteBG="false"
                uploadType="PRACTICE_LOGO"
                labelText="Drag & drop or <span style='text-decoration: underline; cursor: pointer!important;'>browse</span> to upload" 
                fileTypes="image/jpeg, image/png"
              />
              <div v-if="practice.practice_logo" class="is-flex is-justify-content-center">
                <img width="200px" height="200px" :key="logoUrlKey" :src="$root.getURL(practice.practice_logo)" />
              </div>
            </div>
          </div>
          <div class="column">
            <div class="userRow py-3 px-4">

              <div class="mb-2">
                <p @click="editPracticeName()" class="title is-4 is-clickable">
                  {{ practice.practice_name }}
                  <b-icon icon="pencil" type="is-info" />
                </p>
              </div>
              <div class="mb-2">
                <p class="subtitle is-5 mb-1">Get Google Reviews</p>
                <div class="is-flex is-flex-direction-row is-align-items-center">
                  <b-button @click="editReviewsLink()" class="is-info" rounded outlined>
                    <strong>Save your Google Reviews link</strong>  
                  </b-button>
                  <b-tooltip type="is-dark" :active="!practice.google_reviews_link" label="You haven't saved a Google Reviews link">
                    <b-icon
                      class="ml-1"
                      :icon="practice.google_reviews_link ? 'check-circle' : 'alert-circle'"
                      :type="practice.google_reviews_link ? 'is-success' : 'is-danger'"
                    />
                  </b-tooltip>
                </div>
              </div>
              <div class="mb-2">
                <p class="subtitle is-5 mb-1">Link your practice</p>
                <div class="is-flex is-flex-direction-row is-align-items-center">
                  <b-button @click="editPracticeURL()" class="is-info" rounded outlined>
                    <strong>Save your practice's URL</strong>  
                  </b-button>
                  <b-tooltip type="is-dark" :active="!practice.practice_url" label="You haven't saved your practice's URL">
                    <b-icon
                      class="ml-1"
                      :icon="practice.practice_url ? 'check-circle' : 'alert-circle'"
                      :type="practice.practice_url ? 'is-success' : 'is-danger'"
                    />
                  </b-tooltip>
                </div>
              </div>

            </div>
          </div>
        </div>
        <!-- Facebook shareable image uploading -->
        <div class="columns">
          <div class="column">
            <div class="userRow py-3 px-4">
              <facebookImageUpload />
            </div>
          </div>
        </div>
      </div>

      <!-- payment -->
      <div class="bigSection">
        <div  class="columns">
          <div class="column">
            <div class="has-text-left pl-2 pb-3">
              <p class="title is-3">Payments</p>
              <p class="subtitle is-5">Manage your subscription</p>
            </div>
            <stripe />
          </div>
        </div>
      </div>


    </div>
  </div>
</template>

<script>
import addEmployees from "@/components/owner/addEmployees";
import facebookImageUpload from "@/components/owner/facebookImageUpload";
import fileUpload from "@/components/fileUpload/upload.vue";
import { mapState } from 'vuex';
// import { storage } from '@/main';

import stripe from '@/components/owner/stripeComponents';

import { db, references } from '@/main';

export default {
  name: "Account",
  components: {
    addEmployees,
    fileUpload,
    stripe,
    facebookImageUpload,
  },
  computed: {
    ...mapState({
      ready: state => state.auth.isReady,
      practice: state => state.auth.practice,
    })
  },
  data() {
    return {
      videoUrlKey: 0,
      logoUrlKey: 0,
    }
  },
  mounted() {
    this.$root.$on('uploaded_video', this.updateVideo);
    this.$root.$on('uploaded_practice_logo', this.updateLogo);
  },
  methods: {
    updateVideo() { // update firebase urls to beat browser caching
      this.videoUrlKey++;
    },
    updateLogo() { // update firebase urls to beat browser caching
      this.logoUrlKey++;
    },
    editPracticeName() {
      this.$buefy.dialog.prompt({
        inputAttrs: {
          placeholder: this.practice.practice_name
        },
        title: "Edit Practice Name",
        trapFocus: true,
        confirmText: 'Save',
        onConfirm: async(practice_name) => {
          try {
            await db.collection(references.practice).doc(this.practice.practice_id).update({ practice_name });
            this.$buefy.toast.open({ message: 'Saved name change' });
          } catch(message) {
            this.$buefy.toast.open({ type: 'is-danger', message });
          }
        }
      });
    },
    editReviewsLink() {
      this.$buefy.dialog.prompt({
        inputAttrs: {
          placeholder: "Paste here..."
        },
        title: "Google Reviews Link",
        message: 'Follow <a href="https://support.google.com/business/answer/7035772?hl=en" target="_blank">this guide</a> and paste the URL here to encourage Google reviews from your users',
        trapFocus: true,
        confirmText: 'Save',
        onConfirm: async(google_reviews_link) => {
          try {
            await db.collection(references.practice).doc(this.practice.practice_id).update({ google_reviews_link });
            this.$buefy.toast.open({ message: 'Saved link' });
          } catch(message) {
            this.$buefy.toast.open({ type: 'is-danger', message });
          }
        }
      });
    },
    editPracticeURL() {
      this.$buefy.dialog.prompt({
        inputAttrs: {
          placeholder: "https://your_website_here..."
        },
        title: "Edit Practice URL",
        message: "Enter the link to your location's website here, starting with https.<br/> When a patient shares their achievements on Facebook, their post will redirect to this link.",
        trapFocus: true,
        confirmText: "Save",
        onConfirm: async(practice_url) => {
          try {
            await db.collection(references.practice).doc(this.practice.practice_id).update({ practice_url });
            this.$buefy.toast.open({ message: 'Saved URL' });
          } catch(message) {
            this.$buefy.toast.open({ type: 'is-danger', message });
          }
        }
      });
    },
  }
};
</script>

<style lang="scss" scoped>
.pageContent {
  padding-bottom: 6rem;
}
.bigSection {
  margin-top: 4rem;
  // border: dashed red 2px;
}
.userRow {
  background-color: white;
  border-radius: 8px;
  // margin-bottom: 0.5em;
}
</style>